import { uniq, uniqBy } from 'lodash';
import type { APIComponentAttributes, ArdoqId } from '@ardoq/api-types';
import { isTruthy } from '@ardoq/common-helpers';
import { componentInterface } from '@ardoq/component-interface';
import { toIds } from '@ardoq/core';
import { workspaceInterface } from '@ardoq/workspace-interface';
import { referenceInterface } from '@ardoq/reference-interface';
import type { EnhancedScopeData } from '@ardoq/data-model';
import { componentUtils } from '@ardoq/scope-data';
import type {
  ComponentTypeInfo,
  RawGraphNode2,
  ReferenceTypeDetails,
} from '../types';
import { GraphGroup } from '../graphItems/GraphGroup';
import { GraphNode } from '../graphItems/GraphNode';

export const modelTypeKey = (
  modelTypeId: string | number,
  workspaceModelId: string
) => `${modelTypeId}___${workspaceModelId}`;

const componentTypeDetails = (
  componentId: ArdoqId
): null | ComponentTypeInfo => {
  const componentType = componentInterface.getType(componentId);
  const workspaceModelId = workspaceInterface.getWorkspaceModelId(
    componentInterface.getWorkspaceId(componentId) ?? ''
  );
  if (!componentType || !workspaceModelId) {
    return null;
  }
  return { componentType, workspaceModelId };
};

function* componentsTypeDetails(componentIds: ArdoqId[]) {
  for (const componentId of componentIds) {
    const details = componentTypeDetails(componentId);
    if (!details) {
      continue;
    }
    yield details;
  }
}

const componentDetailsTypeKey = ({
  componentType,
  workspaceModelId,
}: ComponentTypeInfo) => modelTypeKey(componentType.id, workspaceModelId);

/** @deprecated use {@link getScopeComponentTypes} */
export const getComponentTypeDetails = (componentIds: ArdoqId[]) =>
  uniqBy(
    Array.from(componentsTypeDetails(componentIds)),
    componentDetailsTypeKey
  );

/** @deprecated use {@link getScopeComponentTypes} */
export const getComponentTypes = (componentIds: ArdoqId[]) =>
  uniqBy(
    uniq(componentIds).map(componentTypeDetails).filter(isTruthy),
    componentDetailsTypeKey
  );

const scopeComponentTypeDetails = (
  enhancedScopeData: EnhancedScopeData,
  componentId: ArdoqId
): null | ComponentTypeInfo => {
  const componentType = componentUtils.getType(enhancedScopeData, componentId);
  const workspaceModelId = componentUtils.getAttribute(
    enhancedScopeData,
    componentId,
    'model'
  );
  if (!componentType || !workspaceModelId) {
    return null;
  }
  return { componentType, workspaceModelId };
};

export const getScopeComponentTypes = (
  enhancedScopeData: EnhancedScopeData,
  componentIds: ArdoqId[]
) =>
  uniqBy(
    uniq(componentIds)
      .map(componentId =>
        scopeComponentTypeDetails(enhancedScopeData, componentId)
      )
      .filter(isTruthy),
    componentDetailsTypeKey
  );

const referenceTypeDetails = (referenceId: ArdoqId) => {
  const ref = referenceInterface.get(referenceId);
  const typeId = ref?.type ?? null;
  const referenceType = referenceInterface.getGlobalReferenceType(referenceId);
  if (typeId === null || referenceType === null) {
    return null;
  }
  const details: ReferenceTypeDetails = {
    referenceType,
    typeId,
    workspaceModelId:
      workspaceInterface.getWorkspaceModelId(ref?.rootWorkspace ?? '') ?? '',
  };
  return details;
};

function* referencesTypeDetails(referenceIds: ArdoqId[]) {
  for (const referenceId of referenceIds) {
    const details = referenceTypeDetails(referenceId);
    if (!details) {
      continue;
    }
    yield details;
  }
}

const referenceDetailsTypeKey = ({
  referenceType,
  workspaceModelId,
}: ReferenceTypeDetails) =>
  modelTypeKey(referenceType?.id ?? '', workspaceModelId);

/** @param referenceIds for best performance, please ensure these are unique. */
export const getReferenceTypeDetails = (referenceIds: ArdoqId[]) =>
  uniqBy(
    Array.from(referencesTypeDetails(referenceIds)),
    referenceDetailsTypeKey
  );

/** @deprecated use {@link getReferenceTypeDetails} */
export const getReferenceTypes = (referenceIds: ArdoqId[]) =>
  uniqBy(
    uniq(referenceIds).map(referenceTypeDetails).filter(isTruthy),
    referenceDetailsTypeKey
  );

export function* descendantIds(item: RawGraphNode2): Iterable<string> {
  yield item.id;
  if (item.children) {
    for (const child of item.children) {
      yield* descendantIds(child);
    }
  }
}

export const hasDescendant = (
  item: RawGraphNode2,
  descendantId: ArdoqId
): boolean =>
  Boolean(
    item.id === descendantId ||
    item.children?.some(child => hasDescendant(child, descendantId))
  );

export const hasDescendantModelId = (
  item: RawGraphNode2,
  descendantModelId: ArdoqId
): boolean =>
  Boolean(
    item.modelId === descendantModelId ||
    item.children?.some(child => hasDescendantModelId(child, descendantModelId))
  );

type CreateGraphItemArgs = {
  item: RawGraphNode2;
  useUniqueId?: boolean;
};
export const createGraphItem = ({ item, useUniqueId }: CreateGraphItemArgs) => {
  const { id, type } = item;
  const modelId = item.modelId ?? '';
  return type
    ? GraphGroup.create(
        modelId,
        item.label ?? '',
        type,
        item.subLabels,
        useUniqueId ? undefined : id
      )
    : GraphNode.create(modelId, item.isGhost, useUniqueId ? undefined : id);
};

type GetRelevantComponentIdsArgs = {
  componentId: ArdoqId;
  workspaceId: ArdoqId;
  childrenByParent: Map<ArdoqId, APIComponentAttributes[]>;
  /** if true, filtered children of the given component will be discarded. */
  discardFilteredChildren?: boolean;
};

export const getRelevantComponentIds = ({
  componentId,
  workspaceId,
  childrenByParent,
  discardFilteredChildren = false,
}: GetRelevantComponentIdsArgs): ArdoqId[] => {
  if (componentId) {
    const children = childrenByParent.get(componentId) ?? [];
    const filteredChildIds = discardFilteredChildren
      ? toIds(
          children.filter(component =>
            componentInterface.isIncludedInContextByFilter(component)
          )
        )
      : toIds(children);

    if (filteredChildIds.length) {
      return filteredChildIds;
    }
    const parentId = componentInterface.getParentId(componentId);
    if (parentId) {
      return getRelevantComponentIds({
        componentId: parentId,
        workspaceId,
        childrenByParent,
        discardFilteredChildren,
      });
    }
  }
  if (workspaceId) {
    return toIds(componentInterface.getRootComponents(workspaceId));
  }

  return [];
};
